<template>
  <div class="product-variant-description">
    <BackstageTooltip
      v-if="showMcaBadge"
      :text="$t('matrix.mca_tooltip_title')"
      :aria-label="$t('matrix.mca_tooltip_title')"
      :title="$t('matrix.mca_tooltip_title')"
      class="mca-badge">
      <BackstageIcon :name="Icons.RECOMMENDED" class="mca-icon" aria-hidden="true" />
    </BackstageTooltip>
    <div
      class="product-fabric-img"
      :class="{ 'no-image': !fabric.imageUrl }"
      @click="fabric.imageUrl ? onImageClick() : undefined">
      <BackstageImageV2 :src="fabric.imageUrl" :alt="fabric.name" />
      <div v-if="fabric.imageUrl" class="expand-image-btn"></div>
    </div>
    <div class="product-fabric-text">
      <BackstageTooltip v-if="isSneakerProgram(fabric)" :text="$t('matrix.sneaker_program_tooltip_title')">
        <p class="strong sneaker-program">{{ fabric.name }}</p>
      </BackstageTooltip>
      <p v-else class="strong">{{ fabric.name }}</p>
      <p>{{ fabric.description }}</p>
    </div>
    <div v-if="fabric.launchDate" class="product-launch-text">
      <p class="strong">{{ t('matrix.product_launch_date') }}</p>
      <p>{{ parseUtcDateToLocaleDate(fabric.launchDate) }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageImageV2 from '@/components/common/BackstageImageV2.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import { useFormat } from '@/composables/format';
import { Icons } from '@/models/icons.enum';
import { FeaturesService } from '@/services/features.service';
import type { Fabric } from '@/types/matrix.interface';
import { TargetedAssortmentTier } from '@onrunning/seasonal-config';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  fabric: Fabric;
}>();

const emit = defineEmits(['show-product-model-details']);
const { parseUtcDateToLocaleDate } = useFormat();
const { t } = useI18n();

function onImageClick() {
  emit('show-product-model-details', props.fabric);
}

function isSneakerProgram(fabric: Fabric): boolean {
  return fabric.targetedAssortments?.includes(TargetedAssortmentTier.SNEAKER_PROGRAM) ?? false;
}

const showMcaBadge = computed(() => FeaturesService.mcaEnabled && props.fabric.isMca);
</script>

<style lang="scss" scoped>
.product-variant-description {
  position: relative;

  .mca-badge {
    position: absolute;
    top: 2px;
    left: 2px;
    width: fit-content;
    line-height: 0;
    z-index: 1;

    .mca-icon {
      width: 16px;
      height: 16px;
    }
  }

  .product-fabric-text p {
    margin: 0;
    font-size: 14px;
    text-align: left;
    color: var(--primary-text-remark-color);
  }

  .product-fabric-text p.strong {
    &.sneaker-program {
      color: var(--vivid-blue-color);
    }
  }

  .product-launch-text {
    margin-left: auto;
    padding: 17px 10px;
    min-width: 82px;
    border-left: 1px solid var(--bg-page-color);

    p {
      margin: 0;
      text-align: center;
      margin-left: auto;
      font-size: 14px;
      line-height: 150%;
      color: var(--primary-text-remark-color);
    }
  }

  .product-launch-text p.strong,
  .product-fabric-text p.strong {
    color: var(--primary-text-color);
    font-weight: 700;
    font-size: 16px;
  }

  .product-fabric-img {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    height: 80px;
    width: 110px;
    cursor: pointer;

    &.no-image {
      cursor: default;
    }

    .expand-image-btn {
      display: block;
    }

    @include breakpoint(medium) {
      .expand-image-btn {
        width: 15px;
        height: 15px;
        top: 0;
        right: 0;
      }
    }

    .backstage-image-container {
      max-width: 80px;
      max-height: 70px;
      width: 80px;
      height: 70px;
      margin: 0px 15px;

      :deep(.placeholder-text) {
        font-size: 0.6rem;
        line-height: 1.2;
      }

      :deep(.placeholder-icon svg) {
        width: 20px;
        height: 20px;
      }
    }
  }

  @media (max-width: 1000px) {
    img {
      margin: 0 5px;
    }

    .product-fabric-text {
      word-break: break-word;
      padding: 0;
      margin: 0 5px;
      min-width: auto;

      p {
        font-size: 10px;
      }
    }

    .product-launch-text {
      padding: 0;
      margin: 0 5px;
      min-width: auto;

      p {
        font-size: 10px;
      }
    }
  }

  @include breakpoint(medium) {
    img {
      width: 60px;
      height: 35px;
      margin: 0;
      text-align: left;
    }

    .product-launch-text {
      padding: 0;
      min-width: auto;
    }

    .product-launch-text p,
    .product-fabric-text p {
      font-size: 10px;
      text-align: left;
    }

    .product-launch-text p.strong,
    .product-fabric-text p.strong {
      font-size: 10px;
      text-align: left;
    }
  }
}
</style>
